import { useState } from 'react'
import { daysInMonthOf, projectMonthEnd } from '@qim/shared'
import { formatMoney } from '../format.js'
import type { CostPeriod, CostSeriesPoint } from '@qim/shared'
import type { JSX, MouseEvent } from 'react'

/**
 * Month-to-date cost against the platform budget — the fleet's Cost tab.
 *
 * ## Hand-rolled SVG, no charting library
 *
 * One series, two reference lines and a marker. A charting dependency would be
 * most of the console's bundle for that, and the CSP is strict enough that
 * several of them (inline styles, `eval`-built formatters) would need it
 * widened. What is here is arithmetic and a dozen elements.
 *
 * ## What it shows, and what it refuses to
 *
 * - **Cumulative, not daily.** The budget is a monthly total, so the line that
 *   meets it is the running total; a daily bar would have to be summed in the
 *   reader's head to answer the only question asked.
 * - **The whole month on the x axis**, so the distance still to go before the
 *   budget line is visible, and the projection has somewhere to go.
 * - **The projection is a straight line and says so** — dashed, lighter, and
 *   labelled "projected" beside the figure. Anything cleverer would look more
 *   authoritative without being more right.
 * - **No budget, no line.** Not a line at zero, and not a placeholder.
 * - **"as of" the last finished day**, because Cost Explorer lags about a day
 *   (Azure Cost Management longer, IM-215) and a line ending yesterday would
 *   otherwise read as a line ending today. The caller says which, as `lag`.
 * - **Accounts that could not be read are counted beside it**, never drawn as
 *   zero — the IM-152 rule the whole cost feature follows.
 *
 * ## Accessible
 *
 * The SVG is `role="img"` with a label that states the numbers, and the daily
 * values are a visually-hidden table beside it, so a screen reader gets the
 * data and not a description of a picture. The summary beside the chart is
 * plain text for everyone. Colours are tokens (`styles.css`), so dark mode is
 * the palette's job, not this file's.
 */

export interface ChartBudget {
  readonly amount: number
  /** Percent of `amount` at which the warning line sits. */
  readonly warnAtPercent?: number
}

const W = 640
const H = 260
const PAD = { top: 16, right: 16, bottom: 32, left: 64 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom

/** A step of 1, 2 or 5 × 10ⁿ that gives about four ticks up to `max`. */
const niceStep = (max: number): number => {
  if (max <= 0) return 1
  const raw = max / 4
  const power = 10 ** Math.floor(Math.log10(raw))
  const unit = raw / power
  return (unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 5 ? 5 : 10) * power
}

/** Short axis labels — `$500`, `$4K`, `$12.5K` — since the summary has the exact figures. */
const tickLabel = (value: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value)
  } catch {
    return formatMoney(value, currency)
  }
}

const dayOf = (date: string): number => Number.parseInt(date.slice(8, 10), 10)

/** The last finished day of a period whose end is exclusive. */
const lastFinished = (series: readonly CostSeriesPoint[]): string | undefined =>
  series[series.length - 1]?.date

export const CostChart = ({
  period,
  series,
  currency,
  budget,
  unreadable,
  lag,
}: {
  readonly period: CostPeriod
  readonly series: readonly CostSeriesPoint[]
  readonly currency: string
  readonly budget?: ChartBudget | undefined
  /** How many accounts are *not* in the line, because they could not be read. */
  readonly unreadable: number
  /** How far behind the line's sources are, one sentence per cloud (`costLagFor`). */
  readonly lag: string
}): JSX.Element => {
  const [hover, setHover] = useState<CostSeriesPoint | undefined>(undefined)

  const monthDays = daysInMonthOf(period)
  const spent = series[series.length - 1]?.cumulative ?? 0
  const projection = projectMonthEnd(period, series)
  const warnLine =
    budget?.warnAtPercent === undefined ? undefined : (budget.amount * budget.warnAtPercent) / 100
  const asOf = lastFinished(series)

  const top = Math.max(spent, projection?.projected ?? 0, budget?.amount ?? 0) * 1.08
  const step = niceStep(top)
  const yMax = Math.max(step, Math.ceil(top / step) * step)
  const ticks = Array.from({ length: Math.round(yMax / step) + 1 }, (_, i) => i * step)

  const x = (day: number): number => PAD.left + ((day - 1) / Math.max(1, monthDays - 1)) * PLOT_W
  const y = (value: number): number => PAD.top + PLOT_H - (value / yMax) * PLOT_H

  const dayTicks = [1, 5, 10, 15, 20, 25, monthDays].filter(
    (d, i, all) =>
      d <= monthDays && all.indexOf(d) === i && !(d !== monthDays && monthDays - d < 3),
  )

  const points = series.map((p) => `${x(dayOf(p.date)).toFixed(1)},${y(p.cumulative).toFixed(1)}`)
  const last = series[series.length - 1]
  const today = last === undefined ? undefined : Math.min(monthDays, dayOf(last.date) + 1)

  const percent = budget === undefined ? undefined : (spent / budget.amount) * 100
  const label = [
    `Month-to-date cost for ${period.from.slice(0, 7)}: ${formatMoney(spent, currency)}`,
    asOf === undefined ? 'no finished day yet' : `as of ${asOf}`,
    budget === undefined
      ? 'no budget set'
      : `${Math.round(percent ?? 0)}% of the ${formatMoney(budget.amount, currency)} budget`,
    projection === undefined
      ? ''
      : `projected ${formatMoney(projection.projected, currency)} by month end on a straight line`,
    unreadable === 0
      ? ''
      : `excluding ${String(unreadable)} account${unreadable === 1 ? '' : 's'} that could not be read`,
  ]
    .filter((part) => part !== '')
    .join(', ')

  const onMove = (event: MouseEvent<SVGRectElement>): void => {
    const box = event.currentTarget.getBoundingClientRect()
    if (box.width === 0 || series.length === 0) return
    const svgX = PAD.left + ((event.clientX - box.left) / box.width) * PLOT_W
    const day = Math.round(1 + ((svgX - PAD.left) / PLOT_W) * (monthDays - 1))
    const nearest = series.reduce((best, p) =>
      Math.abs(dayOf(p.date) - day) < Math.abs(dayOf(best.date) - day) ? p : best,
    )
    setHover(nearest)
  }

  return (
    <figure className="cost-chart">
      <div className="cost-chart-plot">
        <svg viewBox={`0 0 ${String(W)} ${String(H)}`} role="img" aria-label={label}>
          {ticks.map((t) => (
            <g key={t}>
              <line className="chart-grid" x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} />
              <text className="chart-tick" x={PAD.left - 8} y={y(t) + 4} textAnchor="end">
                {tickLabel(t, currency)}
              </text>
            </g>
          ))}
          {dayTicks.map((d) => (
            <text
              key={d}
              className="chart-tick"
              x={x(d)}
              y={H - PAD.bottom + 18}
              textAnchor="middle"
            >
              {d}
            </text>
          ))}

          {warnLine === undefined ? null : (
            <g>
              <line
                className="chart-warn"
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(warnLine)}
                y2={y(warnLine)}
              />
              <text className="chart-ref-label" x={PAD.left + 6} y={y(warnLine) - 5}>
                Warning {budget?.warnAtPercent}% · {tickLabel(warnLine, currency)}
              </text>
            </g>
          )}
          {budget === undefined ? null : (
            <g>
              <line
                className="chart-budget"
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(budget.amount)}
                y2={y(budget.amount)}
              />
              <text className="chart-ref-label" x={PAD.left + 6} y={y(budget.amount) - 5}>
                Budget · {tickLabel(budget.amount, currency)}
              </text>
            </g>
          )}

          {today === undefined ? null : (
            <g>
              <line
                className="chart-today"
                x1={x(today)}
                x2={x(today)}
                y1={PAD.top}
                y2={PAD.top + PLOT_H}
              />
              <text
                className="chart-ref-label"
                x={x(today) + (today > monthDays - 4 ? -4 : 4)}
                y={PAD.top + 10}
                textAnchor={today > monthDays - 4 ? 'end' : 'start'}
              >
                Today
              </text>
            </g>
          )}

          {projection === undefined || last === undefined ? null : (
            <line
              className="chart-projection"
              x1={x(dayOf(last.date))}
              y1={y(last.cumulative)}
              x2={x(monthDays)}
              y2={y(projection.projected)}
            />
          )}
          {points.length === 0 ? null : (
            <polyline className="chart-line" points={points.join(' ')} fill="none" />
          )}
          {last === undefined ? null : (
            <circle
              className="chart-point"
              cx={x(dayOf(last.date))}
              cy={y(last.cumulative)}
              r={4}
            />
          )}

          {hover === undefined ? null : (
            <g className="chart-hover">
              <line
                className="chart-crosshair"
                x1={x(dayOf(hover.date))}
                x2={x(dayOf(hover.date))}
                y1={PAD.top}
                y2={PAD.top + PLOT_H}
              />
              <circle
                className="chart-point"
                cx={x(dayOf(hover.date))}
                cy={y(hover.cumulative)}
                r={4}
              />
            </g>
          )}
          {/* The hit area is the whole plot, not the 2px line. */}
          <rect
            className="chart-hit"
            x={PAD.left}
            y={PAD.top}
            width={PLOT_W}
            height={PLOT_H}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(undefined)}
          />
        </svg>
        {/* Outside the SVG, in text tokens, so it reads in both themes. */}
        <p className="chart-readout muted" aria-hidden="true">
          {hover === undefined
            ? 'Hover the chart for a day’s figures.'
            : `${hover.date}: ${formatMoney(hover.amount, currency)} that day, ${formatMoney(hover.cumulative, currency)} so far`}
        </p>
      </div>

      <figcaption className="cost-chart-summary">
        <dl className="detail">
          <dt>Spent so far</dt>
          <dd>
            <strong>{formatMoney(spent, currency)}</strong>
            <p className="muted">
              {asOf === undefined ? 'No finished day yet this month.' : `As of ${asOf}.`} {lag}
            </p>
          </dd>
          <dt>Budget</dt>
          <dd>
            {budget === undefined ? (
              'No budget is set.'
            ) : (
              <>
                {Math.round(percent ?? 0)}% of {formatMoney(budget.amount, currency)}
                {spent >= budget.amount ? (
                  <p className="muted">
                    <span aria-hidden="true">! </span>Over the budget.
                  </p>
                ) : warnLine !== undefined && spent >= warnLine ? (
                  <p className="muted">
                    <span aria-hidden="true">! </span>Past the warning line.
                  </p>
                ) : null}
              </>
            )}
          </dd>
          <dt>Projected month end</dt>
          <dd>
            {projection === undefined ? (
              '—'
            ) : (
              <>
                {formatMoney(projection.projected, currency)}
                <p className="muted">
                  A projection: the {projection.daysElapsed} day
                  {projection.daysElapsed === 1 ? '' : 's'} so far, continued in a straight line to
                  day {projection.daysInMonth}.
                </p>
              </>
            )}
          </dd>
        </dl>
        {unreadable === 0 ? null : (
          <p className="hint">
            Excluding {unreadable} account{unreadable === 1 ? '' : 's'} that could not be read — the
            real figure is higher by whatever {unreadable === 1 ? 'it' : 'they'} spent.
          </p>
        )}
      </figcaption>

      <table className="visually-hidden">
        <caption>Cost per day, {period.from.slice(0, 7)}</caption>
        <thead>
          <tr>
            <th scope="col">Day</th>
            <th scope="col">That day</th>
            <th scope="col">Month so far</th>
          </tr>
        </thead>
        <tbody>
          {series.map((p) => (
            <tr key={p.date}>
              <th scope="row">{p.date}</th>
              <td>{formatMoney(p.amount, currency)}</td>
              <td>{formatMoney(p.cumulative, currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  )
}
